import { IonContent, IonIcon, IonPage } from '@ionic/react'
import React from 'react'
import styled from 'styled-components'
import { Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { CustomSpacer, FooterButtons, StyleIonHeader, TextStyle } from '../../../../components'
import 'swiper/css'
import 'swiper/css/pagination'
import { DivideLine, DivideLineBold } from '../../../../components/atoms/divide'
import { chevronForwardOutline } from 'ionicons/icons'
import { FreeMode } from 'swiper'
import moment from 'moment-timezone'
import { amountString, ResProductDetail, ResultTradeProduct } from '../../../../common'

type Props = {
  tradeResult: ResultTradeProduct
  productData: ResProductDetail
  onDismiss: () => void
}

export const TradeResultDoneModal: React.FC<Props> = ({ tradeResult, productData, onDismiss }) => {
  const feeRate = 0.011
  const total = Number(tradeResult.tradeOrder?.cost) * Number(tradeResult.tradeOrder?.count)
  const totalWithVat = total * (1 + feeRate)

  const onVerifyTx = (txId: string) => {
    window.open(`https://baobab.scope.klaytn.com/tx/${txId}`, '_blank')?.focus()
  }

  return (
    <IonPage>
      <StyleIonHeader type={'MODAL'} title={''} onDismiss={onDismiss} />
      <IonContent>
        <SectionContainer>
          <TextStyle type="b_24">총 {tradeResult.purchases?.length}번에 걸쳐</TextStyle>
          <br></br>
          <TextStyle type="b_24">거래가 완료되었습니다.</TextStyle>
        </SectionContainer>
        {tradeResult.purchases?.length > 1 ? (
          <StyleSwiper
            slidesPerView={'auto'}
            freeMode={true}
            speed={300}
            modules={[FreeMode, Pagination]}
            pagination={true}
            spaceBetween={7}
          >
            {tradeResult.purchases?.map((d, i) => (
              <SwiperSlide key={i}>
                <ImgWrap>
                  <img
                    src={d.transaction.certificateImgUrl}
                    alt={d.transaction.txId}
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null
                      currentTarget.src = '/img/thumbnail.svg'
                    }}
                  />
                </ImgWrap>
              </SwiperSlide>
            ))}
          </StyleSwiper>
        ) : (
          tradeResult.purchases?.map((d, i) => (
            <ImgWrap2 key={i}>
              <img
                src={d.transaction.certificateImgUrl}
                alt={d.transaction.txId}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null
                  currentTarget.src = '/img/thumbnail.svg'
                }}
              />
            </ImgWrap2>
          ))
        )}
        <SectionContainer>
          <TextStyle type="b_16">총 거래 정보 </TextStyle>
          <CustomSpacer spacing="16px" />
          <BetweenWrap>
            <TextStyle type="r_13" color="gray400">
              거래일시
            </TextStyle>
            <TextStyle type="r_13">
              {`${moment(new Date(tradeResult.tradeOrder?.createdDate)).format('YYYY.MM.DD HH:MM')}`}
            </TextStyle>
          </BetweenWrap>
          <DivideWrap>
            <DivideLine />
          </DivideWrap>
          <BetweenWrap>
            <TextStyle type="r_13" color="gray400">
              작품명
            </TextStyle>
            <TextStyle type="r_13">{productData.name}</TextStyle>
          </BetweenWrap>
          <BetweenWrap>
            <TextStyle type="r_13" color="gray400">
              작가명
            </TextStyle>
            <TextStyle type="r_13">{productData.artist}</TextStyle>
          </BetweenWrap>
          <DivideWrap>
            <DivideLine />
          </DivideWrap>
          <BetweenWrap>
            <TextStyle type="r_13" color="gray400">
              {tradeResult.tradeOrder.type === 'BUY' ? '구매' : '판매'} 조각 수
            </TextStyle>
            <TextStyle type="b_13">{tradeResult.tradeOrder?.count.toLocaleString()} 개</TextStyle>
          </BetweenWrap>
          <BetweenWrap>
            <TextStyle type="r_13" color="gray400"></TextStyle>
            <TextStyle type="r_13">
              체결 {(tradeResult.tradeOrder?.count - tradeResult.tradeOrder?.remainCount).toLocaleString()} 개 /
              미체결&nbsp;
              {tradeResult.tradeOrder?.remainCount.toLocaleString()} 개
            </TextStyle>
          </BetweenWrap>
          <DivideWrap>
            <DivideLine />
          </DivideWrap>
          <BetweenWrap>
            <TextStyle type="r_13" color="gray400">
              단가
            </TextStyle>
            <TextStyle type="r_13">{amountString(String(tradeResult.tradeOrder?.cost))} 원</TextStyle>
          </BetweenWrap>
          <BetweenWrap>
            <TextStyle type="r_13" color="gray400">
              총 금액(수수료 1.1% 포함)
            </TextStyle>
            <TextStyle type="r_13">{Math.floor(totalWithVat).toLocaleString()} 원</TextStyle>
          </BetweenWrap>
        </SectionContainer>
        <DivideLineBold />
        <SectionContainer>
          <TextStyle type="b_16">조각 거래 내역</TextStyle>
          <CustomSpacer spacing="16px" />
          {tradeResult.purchases.map((d, i) => (
            <div key={i}>
              <TitleContainer>
                <CustomChip>
                  <TextStyle type={'sb_11'} color={'white'}>
                    {1 + i}
                  </TextStyle>
                </CustomChip>
                <span className="line" />
              </TitleContainer>
              <BetweenWrap2>
                <TextStyle type="r_13" color="gray400">
                  {tradeResult.tradeOrder.type === 'BUY' ? '구매' : '판매'} 조각 수
                </TextStyle>
                <TextStyle type="r_13">{Math.abs(d.count).toLocaleString()} 개</TextStyle>
              </BetweenWrap2>
              <BetweenWrap2>
                <TextStyle type="r_13" color="gray400">
                  단가
                </TextStyle>
                <TextStyle type="r_13">{d.cost.toLocaleString()} 원</TextStyle>
              </BetweenWrap2>
              <BetweenWrap2 onClick={() => onVerifyTx(d.transaction.txId)}>
                <TextStyle type="r_13" color="gray400">
                  트랜잭션 정보
                </TextStyle>
                <TxInfoContainer>
                  <div className="tx_link">{d.transaction.txId}</div>
                  <IonIcon color="medium" icon={chevronForwardOutline}></IonIcon>
                </TxInfoContainer>
              </BetweenWrap2>
            </div>
          ))}
        </SectionContainer>
        <MsgContainer>
          <TextStyle type="r_13" color="gray400">
            블록체인에 기록된 변조 불가능한 거래내역입니다.
          </TextStyle>
        </MsgContainer>
        <CustomSpacer spacing="30px" />
      </IonContent>
      <FooterButtons
        button={{
          color: 'dark',
          text: '닫기',
          onClick: onDismiss,
        }}
      />
    </IonPage>
  )
}

const ImgWrap = styled.div`
  overflow: hidden;
  img {
    max-width: 340px;
    height: 400px;
    object-fit: contains;
    border: solid 2px var(--ion-color-gray100);
    border-radius: 5px;
    box-shadow: 3px 1px 5px 0px;
  }
`

const ImgWrap2 = styled.div`
  overflow: hidden;
  display: flex;
  justify-content: center;
  padding-bottom: 20px;
  img {
    max-width: 340px;
    height: 400px;
    object-fit: contains;
    border-radius: 10px;
    box-shadow: 3px 1px 5px 0px;
  }
`

const StyleSwiper = styled(Swiper)`
  width: 100%;
  padding: 0 20px 35px 20px;
  margin: 0;
  .swiper-pagination-bullet-active {
    background-color: #000 !important;
  }
  .swiper-slide {
    width: auto;
  }
`

const SectionContainer = styled.div`
  padding: 20px;
`

const BetweenWrap = styled.div`
  display: flex;
  justify-content: space-between;
`

const BetweenWrap2 = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`

const DivideWrap = styled.div`
  padding: 16px 0;
`

const CustomChip = styled.div`
  padding: 2px 12px 6px 12px;
  background-color: var(--ion-color-gray700);
  border-radius: 9px;
  max-height: 18px;
  display: flex;
  align-items: center;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  .line {
    margin-left: 16px;
    border-bottom: solid 1px var(--ion-color-gray200);
    width: 100%;
  }
`

const TxInfoContainer = styled.div`
  max-width: 140px;
  display: flex;
  align-items: center;
  cursor: pointer;
  .tx_link {
    color: var(--ion-color-secondary_main);
    text-decoration: underline;
    text-decoration-color: var(--ion-color-secondary_main);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 13px;
  }
  ion-icon {
    min-width: 16px;
  }
`

const MsgContainer = styled.div`
  width: 100%;
  height: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
`
